import * as PIXI from "pixi.js";

/**
 * SpineAtlasUploader
 *
 * Forces immediate GPU upload of spine atlas page textures.
 *
 * WHY THIS EXISTS:
 * PixiJS uploads textures to GPU VRAM lazily — only when a texture is first
 * rendered. For spine assets with many atlas pages, the first animation that
 * references previously-unrendered pages triggers synchronous gl.texImage2D
 * calls that can exceed the 16ms frame budget, causing RAF warnings and jitter.
 *
 * HOW IT WORKS:
 * Spine atlas pages are stored as TextureAtlas (spine-core) objects in the
 * PIXI Assets cache under "{spineName}-atlas". Each page has a texture chain:
 *
 *   atlas.pages[i].texture  → SpineTexture (extends spine-core Texture)
 *     .texture              → PIXI.Texture
 *       .baseTexture        → PIXI.BaseTexture
 *
 * renderer.texture.bind(baseTexture) forces WebGL to immediately call
 * gl.bindTexture + gl.texImage2D, uploading the decoded image to GPU VRAM.
 *
 * WHEN TO USE:
 * Call after assets are loaded. The renderer does NOT need to be available
 * at call time — the uploader will retry on subsequent frames until it is.
 * Safe to call multiple times — already-uploaded textures are a no-op in WebGL.
 *
 * @example
 *   // Single spine (retries automatically if renderer not ready)
 *   SpineAtlasUploader.upload("background_animation");
 *
 *   // Multiple spines
 *   SpineAtlasUploader.uploadAll(["background_animation", "w1", "h1", "lp"]);
 */

const MAX_RETRY_FRAMES = 30; // ~500ms at 60fps — renderer should be ready well before this
const LOG_PREFIX = "[SpineAtlasUploader]";

class SpineAtlasUploader {
  /**
   * Force-upload all atlas page textures for a single spine asset.
   * If the renderer is not yet available, retries on the next frame
   * (up to MAX_RETRY_FRAMES attempts).
   *
   * @param {string} spineName - The spine asset name (e.g. "background_animation", "w1")
   * @param {number} [_retryCount=0] - Internal retry counter, do not pass manually
   * @returns {void}
   */
  static upload(spineName, _retryCount = 0) {
    try {
      const renderer = globalThis.__PIXI_RENDERER__;
      if (!renderer?.texture) {
        // Renderer not available yet — retry on next frame
        if (_retryCount < MAX_RETRY_FRAMES) {
          if (_retryCount === 0) {
            console.warn(
              `${LOG_PREFIX} ⏳ "${spineName}": renderer not ready, will retry (up to ${MAX_RETRY_FRAMES} frames)`
            );
          }
          requestAnimationFrame(() => {
            SpineAtlasUploader.upload(spineName, _retryCount + 1);
          });
        } else {
          console.error(
            `${LOG_PREFIX} ❌ "${spineName}": renderer still not available after ${MAX_RETRY_FRAMES} retries — GIVING UP. Textures will upload lazily (expect jitter).`
          );
        }
        return;
      }

      const atlasKey = `${spineName}-atlas`;
      const atlas = PIXI.Assets.get(atlasKey);
      if (!atlas?.pages) {
        console.warn(
          `${LOG_PREFIX} ⚠️ "${spineName}": atlas "${atlasKey}" not found in PIXI.Assets cache — skipping.`
        );
        return;
      }

      for (const page of atlas.pages) {
        // SpineTexture.texture → PIXI.Texture → .baseTexture
        const baseTexture = page.texture?.texture?.baseTexture;
        if (baseTexture) {
          renderer.texture.bind(baseTexture);
        }
      }

      // Success — silent (only log warnings/errors)
    } catch (error) {
      console.error(
        `${LOG_PREFIX} ❌ "${spineName}": upload failed with error:`,
        error
      );
    }
  }

  /**
   * Force-upload ONE plain texture (a particle / spritesheet page, not a spine
   * atlas) — the same bind as upload(). Retries while the renderer is not up.
   * Added 2026-10-07: the effects' sheets (flame trail, coin shower) were
   * uploaded on their first use, mid-spin / mid-win.
   *
   * @param {PIXI.BaseTexture|PIXI.Texture} texture
   * @param {number} [_retryCount=0]
   */
  static uploadTexture(texture, _retryCount = 0) {
    try {
      const baseTexture = texture?.baseTexture ?? texture;
      if (!baseTexture || baseTexture.destroyed) return;
      const renderer = globalThis.__PIXI_RENDERER__;
      if (!renderer?.texture) {
        if (_retryCount < MAX_RETRY_FRAMES) {
          requestAnimationFrame(() => SpineAtlasUploader.uploadTexture(baseTexture, _retryCount + 1));
        }
        return;
      }
      renderer.texture.bind(baseTexture);
    } catch {
      // Optimisation only — the texture uploads on its first draw anyway.
    }
  }

  /**
   * Force-upload atlas textures for multiple spine assets.
   * Deduplicates atlas names to avoid redundant uploads for shared spines
   * (e.g. lp symbols that all share the same "lp" atlas).
   *
   * @param {string[]} spineNames - Array of spine asset names
   */
  static uploadAll(spineNames) {
    const unique = [...new Set(spineNames)];
    for (const name of unique) {
      SpineAtlasUploader.upload(name);
    }
  }
}

export default SpineAtlasUploader;
